<template>
  <div class="ep">
    <!-- The page is laid out at a real device width and scaled to fit, so each width shows what visitors see. -->
    <div ref="stage" class="ep-stage" :style="{ height: stageHeight ? stageHeight + 'px' : null }">
      <div ref="frame" :class="['ep-frame', { centered: scale === 1 }]"
           :style="{ width: target + 'px', transform: scale < 1 ? 'scale(' + scale + ')' : null }">
        <div class="ep-bar" aria-hidden="true"><span></span><span></span><span></span><em>{{ hostLabel }} · {{ target }}px</em></div>
        <div class="ep-page">
          <template v-if="!published">
            <p class="pb-note ep-note"><i class="mdi mdi-information-outline pb-ico" aria-hidden="true"></i>
              <span>Sample, not shown on your site. Publish a testimonial to see it here.</span></p>
            <div class="ep-samples">
              <TestimonialCard v-for="item in samples" :key="item.text" :item="item" sample compact />
            </div>
          </template>
          <div v-else ref="host" class="ep-host" :aria-label="'Live preview of ' + (widget ? widget.name : 'the widget')" role="region"></div>
        </div>
      </div>
    </div>
    <p v-if="published && widget && widget.status === 'paused'" class="pb-alert"><i class="mdi mdi-pause-circle-outline pb-ico" aria-hidden="true"></i>
      <span>This widget is paused, so websites show only its fallback link. Turn it back on to show testimonials.</span></p>
  </div>
</template>

<script>
import TestimonialCard from './TestimonialCard.vue';
import { embedScriptUrl, wallUrl } from '../publicUrls';

const WIDTHS = { desktop: 1024, tablet: 768, phone: 390 };
const SAMPLES = [
  { text: 'They answered every question the same day and the result looks better than we hoped.', name: 'Sample customer', company: 'Your customer\'s company', rating: 5 },
  { text: 'Easy to work with, clear prices and no surprises.', name: 'Another sample', rating: 5 },
  { text: 'I would recommend them to anyone who wants it done properly.', byline: 'A customer of your business', rating: 4 }
];

export default {
  name: 'EmbedPreview',
  components: { TestimonialCard },
  props: {
    widget: { type: Object, default: null },
    brand: { type: Object, default: null },
    width: { type: String, default: 'desktop' },
    // Increase after each save: the real embed script is fetched again with a fresh preview nonce.
    reloadKey: { type: Number, default: 0 },
    published: { type: Number, default: 0 }
  },
  data: () => ({ samples: SAMPLES, available: 600, frameHeight: 0, observer: null }),
  computed: {
    target() { return WIDTHS[this.width] || WIDTHS.desktop; },
    scale() { return Math.min(1, this.available / this.target); },
    stageHeight() { return this.frameHeight ? Math.ceil(this.frameHeight * this.scale) : 0; },
    hostLabel() { return this.brand && this.brand.website_url ? String(this.brand.website_url).replace(/^https?:\/\//, '') : 'your-website.com'; }
  },
  watch: {
    reloadKey() { this.inject(); },
    published(value, previous) { if (value && !previous) this.$nextTick(this.inject); },
    widget(value, previous) { if (!previous || !value || value.public_key !== previous.public_key) this.$nextTick(this.inject); }
  },
  mounted() {
    this.measure();
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.$refs.stage);
      this.observer.observe(this.$refs.frame);
    }
    this.inject();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    this.clear();
  },
  methods: {
    measure() {
      if (this.$refs.stage) this.available = Math.max(200, this.$refs.stage.clientWidth);
      if (this.$refs.frame) this.frameHeight = this.$refs.frame.offsetHeight;
    },
    clear() {
      const host = this.$refs.host;
      if (host) while (host.firstChild) host.removeChild(host.firstChild);
    },
    // The same snippet a website pastes: a container with a fallback link, then the script.
    inject() {
      const host = this.$refs.host;
      if (!host || !this.widget || !this.widget.public_key) return;
      this.clear();
      const container = document.createElement('div');
      container.setAttribute('data-trustclap-widget', this.widget.public_key);
      const link = document.createElement('a');
      link.href = wallUrl(this.brand);
      link.textContent = 'Read what customers say about ' + ((this.brand && this.brand.name) || 'us');
      container.appendChild(link);
      host.appendChild(container);
      const script = document.createElement('script');
      script.async = true;
      script.src = embedScriptUrl(this.widget, Date.now().toString(36) + this.reloadKey);
      host.appendChild(script);
    }
  }
};
</script>

<style scoped>
.ep{min-width:0}
.ep-stage{position:relative;overflow:hidden;width:100%}
.ep-frame{border:1px solid #D4C9B8;border-radius:14px;overflow:hidden;background:#fff;box-shadow:var(--pb-shadow-card);transform-origin:top left}
.ep-frame.centered{margin:0 auto}
.ep-bar{display:flex;align-items:center;gap:6px;padding:10px 14px;background:#F3EEE6;border-bottom:1px solid #E6DED2}
.ep-bar span{width:10px;height:10px;border-radius:50%;background:#D4C9B8}
.ep-bar em{margin-left:10px;font:500 12px/1 var(--font-body);font-style:normal;color:#6B635B;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ep-page{padding:32px;min-height:220px;background:#fff}
.ep-note{margin-top:0}
.ep-samples{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(240px,100%),1fr));gap:16px}
</style>
